import React, { useState, useEffect } from 'react';
import { PedidosNuevosAPI } from '../../services/pedidosNuevosService';
import {
  PedidoListaItem,
  FiltrosPedidosNuevos,
  RespuestaListadoPedidos,
  EstadoPedidoNuevo,
} from '../../types/pedidosNuevos';
import TarjetaPedido from '../TarjetaPedido';
import ConfirmModal from '../modals/ConfirmModal';
import { useNavigate } from 'react-router-dom';
import { getHoyLocal } from '../../utils/fechaActual';
import {
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Download,
  AlertCircle,
  HelpCircle,
} from 'lucide-react';

/**
 * Tab para administrador: gestión completa de todos los pedidos
 * con filtros avanzados y exportación
 */
const GestionCompletaTab: React.FC = () => {
  const navigate = useNavigate();
  const [pedidos, setPedidos] = useState<PedidoListaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Paginación y filtros
  const [paginaActual, setPaginaActual] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalPedidos, setTotalPedidos] = useState(0);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<EstadoPedidoNuevo | ''>('');
  const [filtroCliente, setFiltroCliente] = useState<number | ''>('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [soloTranscritos, setSoloTranscritos] = useState(false);
  // Fix tanda2 T2-15 (2026-06-30): antes el default era `false` →
  // filtro implícito `fecha_entrega >= hoy` → si no había pedidos
  // futuros la vista mostraba "0 pedidos" aunque el histórico tuviera
  // decenas. Confundía al admin (creía que no había datos). Ahora el
  // default es `true` → se ve TODO el histórico + los vencidos con
  // badge, y el admin puede desmarcar para filtrar sólo futuros. Los
  // vencidos ya se distinguen visualmente por su badge, así que la
  // señal de urgencia se conserva sin ocultar información.
  const [incluirVencidos, setIncluirVencidos] = useState(true);

  // Estados para modales
  const [modalMensaje, setModalMensaje] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info' as 'warning' | 'danger' | 'info',
  });

  const cargarPedidos = async () => {
    setLoading(true);
    setError(null);

    try {
      // Si el admin no puso un rango manual, aplicamos vigencia por
      // default (fecha_entrega >= hoy). Si quiere ver vencidos
      // marca el checkbox y se levanta el filtro implícito.
      const fechaDesdeEfectiva = fechaDesde
        ? fechaDesde
        : incluirVencidos
          ? undefined
          : getHoyLocal();

      const filtros: FiltrosPedidosNuevos = {
        estado: filtroEstado || undefined,
        clienteId: filtroCliente || undefined,
        fechaDesde: fechaDesdeEfectiva,
        fechaHasta: fechaHasta || undefined,
        busqueda: busqueda || undefined,
        page: paginaActual,
        limit: 15,
      };

      const respuesta: RespuestaListadoPedidos =
        await PedidosNuevosAPI.listarPedidos(filtros);

      let pedidosFiltrados = respuesta.data;

      // Filtro adicional de transcritos (client-side)
      if (soloTranscritos) {
        pedidosFiltrados = pedidosFiltrados.filter((p) => p.es_transcripcion);
      }

      setPedidos(pedidosFiltrados);
      setTotalPaginas(respuesta.pagination.totalPages);
      setTotalPedidos(respuesta.pagination.total);
    } catch (err: any) {
      console.error('Error al cargar pedidos:', err);
      setError(err.message || 'Error al cargar los pedidos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarPedidos();
  }, [
    paginaActual,
    filtroEstado,
    filtroCliente,
    soloTranscritos,
    incluirVencidos,
  ]);

  const handleBuscar = () => {
    setPaginaActual(1);
    cargarPedidos();
  };

  const handleLimpiarFiltros = () => {
    setBusqueda('');
    setFiltroEstado('');
    setFiltroCliente('');
    setFechaDesde('');
    setFechaHasta('');
    setSoloTranscritos(false);
    setIncluirVencidos(false);
    setPaginaActual(1);
  };

  const handleVerDetalle = (pedido: PedidoListaItem) => {
    navigate(`/pedidos/${pedido.id}`);
  };

  const handleExportar = () => {
    // TODO: Implementar exportación a Excel/CSV
    setModalMensaje({
      isOpen: true,
      title: 'Próximamente',
      message: 'Funcionalidad de exportación próximamente',
      type: 'info',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Gestión Completa de Pedidos
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {totalPedidos} pedido{totalPedidos !== 1 ? 's' : ''} encontrado
            {totalPedidos !== 1 ? 's' : ''}
          </p>
        </div>

        <button
          onClick={handleExportar}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <Download className="w-4 h-4" />
          <span>Exportar</span>
        </button>
      </div>

      {/* Filtros avanzados */}
      <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Búsqueda */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Buscar
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleBuscar()}
                placeholder="Código, cliente, minuta..."
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
              <button
                onClick={handleBuscar}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Search className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Estado */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Estado
            </label>
            <select
              value={filtroEstado}
              onChange={(e) => {
                setFiltroEstado(e.target.value as EstadoPedidoNuevo | '');
                setPaginaActual(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="">Todos</option>
              <option value="borrador">Borrador</option>
              <option value="solicitado">Solicitado</option>
              <option value="en_preparacion">En Cocina</option>
              <option value="listo">Listo</option>
              <option value="despachado">Despachado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>

        </div>

        {/* Opciones extra: filtros booleanos agrupados con tooltip
            explicativo. Quedan en una fila aparte de los selects de
            filtro principal para que se identifiquen como "switches"
            independientes del usuario admin. */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Opciones extra
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={soloTranscritos}
                onChange={(e) => {
                  setSoloTranscritos(e.target.checked);
                  setPaginaActual(1);
                }}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Solo transcritos
              </span>
              <span
                title="Pedidos cargados por un admin u operador en nombre del cliente (modo transcripción), no creados por el cliente mismo."
                className="inline-flex"
              >
                <HelpCircle
                  className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  aria-hidden="true"
                />
                <span className="sr-only">
                  Pedidos cargados por un admin u operador en nombre del
                  cliente (modo transcripción).
                </span>
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={incluirVencidos}
                onChange={(e) => {
                  setIncluirVencidos(e.target.checked);
                  setPaginaActual(1);
                }}
                className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Incluir vencidos
              </span>
              <span
                title="Muestra también pedidos cuya fecha de entrega ya pasó."
                className="inline-flex"
              >
                <HelpCircle
                  className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  aria-hidden="true"
                />
                <span className="sr-only">
                  Muestra también pedidos cuya fecha de entrega ya pasó.
                </span>
              </span>
            </label>
          </div>
        </div>

        {/* Fechas */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fecha desde
            </label>
            <input
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fecha hasta
            </label>
            <input
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
        </div>

        {/* Acciones de filtros */}
        <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleLimpiarFiltros}
            className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            Limpiar filtros
          </button>

          <button
            onClick={cargarPedidos}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refrescar</span>
          </button>
        </div>
      </div>

      {/* Mensajes de estado */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      )}

      {/* Lista de pedidos */}
      {!loading && pedidos.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400 text-lg">
            No se encontraron pedidos con los filtros aplicados
          </p>
          <button
            onClick={handleLimpiarFiltros}
            className="mt-4 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
          >
            Limpiar filtros
          </button>
        </div>
      )}

      {!loading && pedidos.length > 0 && (
        <div className="space-y-4">
          {pedidos.map((pedido) => {
            // Vencido = fecha_entrega < hoy (zona del usuario).
            const hoyStr = getHoyLocal();
            const fe = (pedido.fecha_entrega || '').slice(0, 10);
            const esVencido = !!fe && fe < hoyStr;
            return (
              <div key={pedido.id} className="relative">
                {esVencido && (
                  <span
                    className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 border border-red-300 dark:border-red-700 flex items-center gap-1 shadow-sm"
                    title="Fecha de entrega ya pasó"
                  >
                    <AlertCircle className="w-3 h-3" />
                    Vencido
                  </span>
                )}
                <TarjetaPedido
                  pedido={pedido}
                  onVerDetalle={handleVerDetalle}
                  mostrarCliente={true}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* Paginación */}
      {totalPaginas > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Página {paginaActual} de {totalPaginas}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setPaginaActual((p) => Math.max(1, p - 1))}
              disabled={paginaActual === 1}
              className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Anterior</span>
            </button>

            <button
              onClick={() =>
                setPaginaActual((p) => Math.min(totalPaginas, p + 1))
              }
              disabled={paginaActual === totalPaginas}
              className="flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <span>Siguiente</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Modal de Mensaje */}
      <ConfirmModal
        isOpen={modalMensaje.isOpen}
        onClose={() => setModalMensaje({ ...modalMensaje, isOpen: false })}
        onConfirm={() => setModalMensaje({ ...modalMensaje, isOpen: false })}
        title={modalMensaje.title}
        message={modalMensaje.message}
        type={modalMensaje.type}
        confirmText="Aceptar"
        cancelText=""
      />
    </div>
  );
};

export default GestionCompletaTab;
